// GlobalChatbot.js - Universal chatbot accessible from anywhere
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import API_BASE_URL from '../config';
import {
  Box,
  TextField,
  Typography,
  Avatar,
  IconButton,
  InputAdornment,
  Fade,
  Fab,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';
import DeleteIcon from '@mui/icons-material/Delete';
import { useChatbot, useChatbotState, useChatbotActions } from '../contexts/ChatbotContext';

function Line({ content, visible }) {
  return (
    <Fade in={visible} timeout={800}>
      <Box sx={{ width: '100%', mb: 1 }}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
      </Box>
    </Fade>
  );
}

// Styled component for the chat container
const StyledChatContainer = styled(Box)(() => ({
  flex: 1,
  overflowY: 'auto',
  backgroundColor: '#FAF3E0',
  padding: 0,
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#FAF3E0',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#8D7B68',
    borderRadius: '8px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: '#8D7B68',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: '#8D7B68 #FAF3E0',
}));

function GlobalChatbot() {
  const {
    isOpen,
    currentBook,
    currentPage,
    conversationId,
    closeChatbot,
    resetConversation,
  } = useChatbot();

  const navigate = useNavigate();
  const [chatHistory, setChatHistory] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [progressMessage, setProgressMessage] = useState('Contemplating...');
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);
  const chatContainerRef = useRef(null);

  const token = localStorage.getItem('token');

  // Load chat history from localStorage on mount
  useEffect(() => {
    const storedHistory = localStorage.getItem('chatbot_history');
    if (storedHistory) {
      try {
        setChatHistory(JSON.parse(storedHistory));
      } catch (err) {
        console.error('Failed to parse chat history:', err);
      }
    }
  }, []);

  // Save chat history to localStorage whenever it changes
  useEffect(() => {
    if (chatHistory.length > 0) {
      localStorage.setItem('chatbot_history', JSON.stringify(chatHistory));
    }
  }, [chatHistory]);

  useEffect(() => {
    if (chatContainerRef.current) {
      const container = chatContainerRef.current;
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;

      if (shouldScrollToBottom || isNearBottom) {
        container.scrollTop = container.scrollHeight;
        setShouldScrollToBottom(false);
      }
    }
  }, [chatHistory, shouldScrollToBottom]);

  const handleSend = async () => {
    if (!userInput.trim()) return;

    const newHistory = [...chatHistory, { role: 'user', content: userInput }];
    setChatHistory(newHistory);
    const currentInput = userInput;
    setUserInput('');
    setIsLoading(true);
    setProgressMessage('Contemplating...');
    setShouldScrollToBottom(true);

    setChatHistory((prevHistory) => [
      ...prevHistory,
      { role: 'assistant', content: '', lines: [] },
    ]);

    try {
      const response = await fetch(`${API_BASE_URL}/gpt/ask-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          bookDetails: currentBook || null,
          userQuestion: currentInput,
          conversationId,
          pageContext: currentPage,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get response from server');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = JSON.parse(line.substring(6));

            if (data.type === 'progress') {
              setProgressMessage(data.message);
            } else if (data.type === 'navigate') {
              // Handle navigation command
              const { page, bookId } = data.navigation;
              if (page === 'home') {
                navigate('/home');
              } else if (page === 'my-books') {
                navigate('/my-books');
              } else if (page === 'search') {
                navigate('/search');
              } else if (page === 'book-details' && bookId) {
                navigate(`/book/${bookId}`);
              }
            } else if (data.type === 'response') {
              // Final response received
              const assistantReply = data.reply;
              setChatHistory((prevHistory) => {
                const newHistory = [...prevHistory];
                const lastIndex = newHistory.length - 1;
                if (newHistory[lastIndex] && newHistory[lastIndex].role === 'assistant') {
                  newHistory[lastIndex].content = assistantReply;
                  newHistory[lastIndex].lines = [{ content: assistantReply, visible: true }];
                }
                return newHistory;
              });
              setIsLoading(false);
            } else if (data.type === 'error') {
              console.error('Error from server:', data.message);
              setIsLoading(false);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setChatHistory((prevHistory) => {
        const newHistory = [...prevHistory];
        const lastIndex = newHistory.length - 1;
        if (newHistory[lastIndex] && newHistory[lastIndex].role === 'assistant') {
          newHistory[lastIndex].content =
            'My apologies! An error occurred while contemplating your query. Please try again.';
          newHistory[lastIndex].lines = [{ content: newHistory[lastIndex].content, visible: true }];
        }
        return newHistory;
      });
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClearHistory = () => {
    if (window.confirm('Are you sure you want to clear the entire conversation history?')) {
      setChatHistory([]);
      localStorage.removeItem('chatbot_history');
      resetConversation();
    }
  };

  if (!isOpen) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        width: { xs: 'calc(100vw - 48px)', sm: 450, md: 500 },
        height: { xs: 'calc(100vh - 100px)', sm: 600, md: 650 },
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#FAF3E0',
        borderRadius: '16px',
        boxShadow: '0 12px 30px rgba(59, 46, 34, 0.28), 0 2px 8px rgba(59, 46, 34, 0.2)',
        border: '1px solid rgba(59, 46, 34, 0.55)',
        zIndex: 1300,
        overflow: 'hidden',
        fontFamily: "'Cinzel', 'Trajan Pro', Georgia, serif",
      }}
    >
      {/* Elegant Header */}
      <Box
        sx={{
          p: 3,
          pr: 6,
          pb: 2,
          backgroundColor: '#FAF3E0',
          borderBottom: '1px solid rgba(141, 123, 104, 0.4)',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '-4px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '48px',
            height: '1px',
            backgroundColor: '#8D7B68',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography
            variant="h4"
            sx={{
              fontFamily: "'Cinzel', 'Trajan Pro', Georgia, serif",
              fontWeight: 500,
              textAlign: 'center',
              color: '#2C2416',
              letterSpacing: '0.08em',
              fontSize: { xs: '1.3rem', md: '1.5rem' },
            }}
          >
            Diary
          </Typography>
          <Box sx={{ position: 'absolute', right: 8, top: 8, display: 'flex', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={handleClearHistory}
              sx={{
                color: '#8D7B68',
                '&:hover': { backgroundColor: 'rgba(141, 123, 104, 0.1)' },
              }}
              title="Clear history"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={closeChatbot}
              sx={{
                color: '#3b2e22',
                '&:hover': { backgroundColor: 'rgba(141, 123, 104, 0.1)' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'center',
            color: '#8D7B68',
            fontFamily: "'Crimson Text', Georgia, serif",
            fontStyle: 'italic',
            mt: 0.5,
            fontSize: '0.85rem',
          }}
        >
          {currentBook ? currentBook.title : `On ${currentPage?.name || 'Your Library'}`}
        </Typography>
      </Box>

      {/* Chat Messages */}
      <StyledChatContainer ref={chatContainerRef}>
        {chatHistory.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              px: 4,
            }}
          >
            <Typography
              sx={{
                fontFamily: "'IM Fell English', 'Crimson Text', Georgia, serif",
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                color: '#8D7B68',
                textAlign: 'center',
                fontStyle: 'italic',
                lineHeight: 1.8,
              }}
            >
              "A room without books is like a body without a soul."
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Crimson Text', Georgia, serif",
                fontSize: '0.9rem',
                color: '#A0937D',
                textAlign: 'center',
                mt: 2,
              }}
            >
              Begin your discourse...
            </Typography>
          </Box>
        ) : (
          chatHistory.map((msg, index) => {
            const isAssistantPlaceholder =
              msg.role === 'assistant' &&
              (!msg.content || msg.content.trim() === '') &&
              (!msg.lines || msg.lines.length === 0);
            if (isAssistantPlaceholder && isLoading) {
              return null;
            }
            return (
              <Box key={index}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                    alignItems: 'flex-start',
                    px: { xs: 2, md: 3 },
                    py: 2,
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: 'transparent',
                      border: `2px solid ${msg.role === 'user' ? '#333' : '#8D7B68'}`,
                      mt: 0.5,
                      color: msg.role === 'user' ? '#333' : '#8D7B68',
                      width: { xs: 30, md: 36 },
                      height: { xs: 30, md: 36 },
                    }}
                  >
                    {msg.role === 'user' ? <PersonIcon fontSize="small" /> : <SmartToyIcon fontSize="small" />}
                  </Avatar>
                  <Box
                    sx={{
                      maxWidth: '85%',
                      mx: 2,
                      fontFamily:
                        msg.role === 'assistant'
                          ? "'Libre Baskerville', 'Crimson Text', 'Palatino Linotype', Georgia, serif"
                          : "'Crimson Text', Georgia, serif",
                      color: '#2C2416',
                      fontSize: msg.role === 'assistant' ? { xs: '0.9rem', md: '0.95rem' } : { xs: '0.95rem', md: '1rem' },
                      lineHeight: 1.7,
                    }}
                  >
                    {msg.role === 'assistant' && msg.lines ? (
                      msg.lines.map((lineObj, lineIndex) => (
                        <Line
                          key={lineIndex}
                          content={lineObj.content}
                          visible={lineObj.visible}
                        />
                      ))
                    ) : (
                      <Typography
                        variant="body1"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          fontFamily: 'inherit',
                          fontSize: 'inherit',
                          lineHeight: 'inherit',
                        }}
                      >
                        {msg.content}
                      </Typography>
                    )}
                  </Box>
                </Box>
                {/* Elegant Divider */}
                {index < chatHistory.length - 1 && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 3,
                      py: 1,
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        height: '1px',
                        background: 'linear-gradient(to right, transparent, #C4B5A0 50%, transparent)',
                      }}
                    />
                    <Box
                      sx={{
                        width: '4px',
                        height: '4px',
                        backgroundColor: '#8D7B68',
                        borderRadius: '50%',
                        mx: 2,
                      }}
                    />
                    <Box
                      sx={{
                        flex: 1,
                        height: '1px',
                        background: 'linear-gradient(to left, transparent, #C4B5A0 50%, transparent)',
                      }}
                    />
                  </Box>
                )}
              </Box>
            );
          })
        )}
        {/* Loading Indicator */}
        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 3,
              py: 2,
            }}
          >
            <Avatar
              sx={{
                bgcolor: 'transparent',
                border: '2px solid #8D7B68',
                color: '#8D7B68',
                width: 36,
                height: 36,
              }}
            >
              <SmartToyIcon fontSize="small" />
            </Avatar>
            <Box sx={{ mx: 2 }}>
              <Typography
                sx={{
                  fontFamily: "'Crimson Text', 'Libre Baskerville', Georgia, serif",
                  fontStyle: 'italic',
                  color: '#8D7B68',
                  fontSize: '0.95rem',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 0.4 },
                    '50%': { opacity: 1 },
                  },
                }}
              >
                {progressMessage}
              </Typography>
            </Box>
          </Box>
        )}
      </StyledChatContainer>

      {/* Elegant Input Area */}
      <Box
        sx={{
          p: { xs: 2, md: 2.5 },
          borderTop: '2px solid #8D7B68',
          backgroundColor: '#FAF3E0',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-4px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '60px',
            height: '2px',
            backgroundColor: '#8D7B68',
          },
        }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={3}
          placeholder="Inscribe your thoughts upon this tome..."
          value={userInput}
          onChange={(e) => setUserInput(e.target.value)}
          onKeyDown={handleKeyPress}
          disabled={isLoading}
          variant="outlined"
          sx={{
            backgroundColor: '#F4ECE3',
            fontFamily: "'Crimson Text', Georgia, serif",
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#8D7B68',
                borderWidth: '2px',
              },
              '&:hover fieldset': {
                borderColor: '#7A6859',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#8D7B68',
                borderWidth: '2px',
              },
            },
            '& .MuiInputBase-input': {
              fontFamily: "'Crimson Text', Georgia, serif",
              fontSize: '0.95rem',
              color: '#2C2416',
            },
            '& .MuiInputBase-input::placeholder': {
              fontStyle: 'italic',
              color: '#A0937D',
              opacity: 1,
            },
          }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end" sx={{ mr: 0.5 }}>
                  <IconButton
                    onClick={handleSend}
                    disabled={isLoading || !userInput.trim()}
                    sx={{
                      color: '#FAF3E0',
                      backgroundColor: '#333',
                      width: { xs: 34, md: 36 },
                      height: { xs: 34, md: 36 },
                      '&:hover': { backgroundColor: '#8D7B68' },
                      '&:disabled': {
                        backgroundColor: '#C4B5A0',
                        color: '#E5DED3',
                      },
                    }}
                  >
                    <SendIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
    </Box>
  );
}

// Floating Action Button to open chatbot
export function ChatbotFAB() {
  const { isOpen } = useChatbotState();
  const { openChatbot } = useChatbotActions();

  if (isOpen) return null;

  return (
    <Fab
      color="primary"
      aria-label="open diary"
      onClick={() => openChatbot()}
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 1200,
        backgroundColor: '#8D7B68',
        '&:hover': {
          backgroundColor: '#7A6859',
          transform: 'scale(1.1)',
        },
        transition: 'all 0.2s ease',
      }}
    >
      <ChatIcon />
    </Fab>
  );
}

export default GlobalChatbot;
